import { RecordSignUpArrivalRequest } from "./contracts/RecordSignUpArrivalRequest";

/**
 * A marketing-site CTA hands a visitor to the app with
 * `?utm_source=marketing-site&utm_medium=cta&utm_campaign=<placement>&lang=<locale>`
 * (plus `fbclid` after a Meta ad click). Turning that arrival into one anonymous
 * counter row is the mid-funnel number between the ad platform's landing-page
 * views and our own trial starts. Nothing identifying is read or sent, so this
 * runs before any consent. A reload of the same arrival in the same tab is not
 * counted twice.
 */
const MARKETING_SOURCE = "marketing-site";
const CTA_MEDIUM = "cta";
const REPORTED_KEY = "signUpArrivalReported";
/** The marketing site adds `uncounted=1` when the browser opted out of the counters (the team's own visits). */
const UNCOUNTED_PARAM = "uncounted";

const PLACEMENT_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
const LOCALE_PATTERN = /^[a-z]{2}(-[A-Za-z]{2})?$/;
const FBCLID_PATTERN = /^[A-Za-z0-9_-]{8,400}$/;

/** The arrival described by the query string, or null when it is not a marketing CTA hand-off. */
export const readSignUpArrival = (search: string): RecordSignUpArrivalRequest | null => {
  const params = new URLSearchParams(search);
  if (params.get("utm_source") !== MARKETING_SOURCE || params.get("utm_medium") !== CTA_MEDIUM) {
    return null;
  }
  if (params.get(UNCOUNTED_PARAM) === "1") {
    return null;
  }
  const placement = (params.get("utm_campaign") ?? "").trim().toLowerCase();
  if (!PLACEMENT_PATTERN.test(placement)) {
    return null;
  }
  const lang = params.get("lang");
  const fbclid = params.get("fbclid");
  return {
    placement,
    locale: lang && LOCALE_PATTERN.test(lang) ? lang : null,
    hasMetaClick: !!fbclid && FBCLID_PATTERN.test(fbclid),
  };
};

/** True when this tab already reported the given arrival (a reload, a back navigation). */
export const wasSignUpArrivalReported = (search: string): boolean => {
  try {
    return sessionStorage.getItem(REPORTED_KEY) === search;
  } catch {
    return false;
  }
};

export const markSignUpArrivalReported = (search: string): void => {
  try {
    sessionStorage.setItem(REPORTED_KEY, search);
  } catch {
    // Storage unavailable — a reload may count twice; acceptable for a counter.
  }
};
